/**
 * Edit One-Time Job Modal
 * Allows company administrators to edit existing one-time jobs
 */

import { useState, useEffect, useRef, type FormEvent } from 'react';

function toLocalDateTimeString(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
import type { OneTimeJob } from '../../../../../shared/types';
import { updateOneTimeJob } from '../../../services/oneTimeJob.service';
import { ErrorMessage } from '../../ErrorMessage';
import { BaseModal } from '../../common/BaseModal';
import { FormInputField } from '../../form/FormInputField';
import { FormTextareaField } from '../../form/FormTextareaField';
import { LocationAutocompleteField } from '../../form/LocationAutocompleteField';
import { ModalButtonGroup } from '../../common/ModalButtonGroup';
import { ConfirmationDialog } from '../../ConfirmationDialog';

interface EditOneTimeJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  oneTimeJob: OneTimeJob | null;
  onSuccess: () => void;
}

export function EditOneTimeJobModal({ isOpen, onClose, oneTimeJob, onSuccess }: EditOneTimeJobModalProps) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: {
      address: '',
      latitude: 0,
      longitude: 0,
    },
    scheduledStart: new Date(),
    scheduledEnd: new Date(),
    hourlyRate: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmDialog, setConfirmDialog] = useState<{ title: string; message: string; onConfirm: () => void } | null>(null);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  // Populate form when one-time job changes
  useEffect(() => {
    if (oneTimeJob) {
      setFormData({
        title: oneTimeJob.title,
        description: oneTimeJob.description || '',
        location: oneTimeJob.location,
        scheduledStart: oneTimeJob.scheduledStart,
        scheduledEnd: oneTimeJob.scheduledEnd,
        hourlyRate: oneTimeJob.hourlyRate,
      });
    }
  }, [oneTimeJob]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!oneTimeJob) return;

    const data = formDataRef.current;

    // Validation
    if (!data.title.trim()) {
      setError('One-time job title is required');
      return;
    }

    if (!data.location.address.trim()) {
      setError('Location address is required');
      return;
    }

    if (data.location.latitude === 0 && data.location.longitude === 0) {
      setError('Please select an address from the suggestions to set GPS coordinates');
      return;
    }

    if (data.hourlyRate <= 0) {
      setError('Hourly rate must be greater than 0');
      return;
    }

    if (data.scheduledEnd <= data.scheduledStart) {
      setError('Scheduled end must be after scheduled start');
      return;
    }

    // Warn if editing assigned or completed one-time job
    if (oneTimeJob.assignmentStatus !== 'unassigned') {
      const assignmentType = oneTimeJob.assignmentStatus === 'assigned_to_manager' ? 'a manager' : 'staff';
      setConfirmDialog({
        title: 'Edit Assigned Job',
        message: `This one-time job is currently assigned to ${assignmentType}. Changing the schedule or details may cause confusion.`,
        onConfirm: () => { setConfirmDialog(null); performUpdate(data); },
      });
      return;
    }

    if (oneTimeJob.status === 'completed') {
      setConfirmDialog({
        title: 'Edit Completed Job',
        message: 'This one-time job is marked as completed. Are you sure you want to edit it?',
        onConfirm: () => { setConfirmDialog(null); performUpdate(data); },
      });
      return;
    }

    performUpdate(data);
  };

  const performUpdate = async (data: typeof formData) => {
    if (!oneTimeJob) return;
    setLoading(true);
    try {
      await updateOneTimeJob({
        oneTimeJobId: oneTimeJob.id,
        companyId: oneTimeJob.companyId,
        ...data,
      });
      onSuccess();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update one-time job');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !oneTimeJob) return null;

  return (
    <>
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit One-Time Job"
      size="medium"
      loading={loading}
    >
      <form onSubmit={handleSubmit}>
        <FormInputField
          label="One-Time Job Title"
          value={formData.title}
          onChange={(value) => setFormData({ ...formData, title: value })}
          placeholder="e.g., Night Shift Security Guard"
          required
        />

        <FormTextareaField
          label="Description"
          value={formData.description}
          onChange={(value) => setFormData({ ...formData, description: value })}
          placeholder="One-time job details and requirements..."
          rows={3}
        />

        <LocationAutocompleteField
          key={oneTimeJob.id}
          label="Location Address"
          initialValue={oneTimeJob.location.address}
          onChange={(result) => setFormData((prev) => ({
            ...prev,
            location: result,
          }))}
          required
        />

        <FormInputField
          label="Scheduled Start"
          type="datetime-local"
          value={toLocalDateTimeString(formData.scheduledStart)}
          onChange={(value) => setFormData({
            ...formData,
            scheduledStart: new Date(value)
          })}
          required
        />

        <FormInputField
          label="Scheduled End"
          type="datetime-local"
          value={toLocalDateTimeString(formData.scheduledEnd)}
          onChange={(value) => setFormData({
            ...formData,
            scheduledEnd: new Date(value)
          })}
          required
        />

        <FormInputField
          label="Hourly Rate (€)"
          type="number"
          value={formData.hourlyRate.toString()}
          onChange={(value) => setFormData({
            ...formData,
            hourlyRate: parseFloat(value) || 0
          })}
          min="0"
          step="0.01"
          placeholder="15.00"
          required
        />

        {error && <ErrorMessage message={error} />}

        <ModalButtonGroup
          onCancel={onClose}
          submitLabel="Save Changes"
          isLoading={loading}
          loadingLabel="Saving..."
        />
      </form>
    </BaseModal>

    {confirmDialog && (
      <ConfirmationDialog
        isOpen={true}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel="Continue"
        confirmStyle="warning"
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(null)}
      />
    )}
    </>
  );
}
